import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Calculator, Timer } from 'lucide-react';

const creditPackages = [
  { credits: 60, price: 18000, pricePerCredit: 300 },
  { credits: 120, price: 33600, pricePerCredit: 280 },
  { credits: 300, price: 78000, pricePerCredit: 260 }
];

export const PricingCalculator = () => {
  const [selectedCredits, setSelectedCredits] = useState(60);
  

  // Calculate price using "drops by 10 SEK for every 100 credits" rule
  const calculatePrice = (credits: number) => {
    let totalPrice = 0;
    let remainingCredits = credits;
    
    // First tier: 1-99 credits at 300 SEK each
    if (remainingCredits > 0) {
      const creditsInTier = Math.min(remainingCredits, 99);
      totalPrice += creditsInTier * 300;
      remainingCredits -= creditsInTier;
    }
    
    // Subsequent tiers: every 100 credits, price drops by 20 SEK
    let currentPrice = 280; // Starting at 280 for 100-199 range
    while (remainingCredits > 0) {
      const creditsInTier = Math.min(remainingCredits, 100);
      totalPrice += creditsInTier * currentPrice;
      remainingCredits -= creditsInTier;
      currentPrice = Math.max(260, currentPrice - 20); // Minimum 260 SEK per credit
    }
    
    return totalPrice;
  };

  // Calculate current price per credit for the selected tier
  const getCurrentPricePerCredit = (credits: number) => {
    if (credits <= 99) return 300;
    if (credits <= 199) return 280;
    return 260; // 200+ credits
  };

  // Calculate credits needed for desired seconds
  const calculateCreditsForSeconds = (seconds: number) => {
    return seconds; // 1 credit = 1 second based on the examples
  };


  const currentPrice = calculatePrice(selectedCredits);
  const currentPricePerCredit = getCurrentPricePerCredit(selectedCredits);
  const availableSeconds = selectedCredits;


  return (
    <div className="w-full max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
          Priskalylator
        </h2>
        <p className="text-muted-foreground">
          Beräkna ditt pris baserat på våra stafflade priser
        </p>
      </div>

      {/* Credit Wheel/Slider */}
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Välj Credits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <Label>Antal Credits: {selectedCredits}</Label>
            <Slider
              value={[selectedCredits]}
              onValueChange={(value) => setSelectedCredits(value[0])}
              max={500}
              min={10}
              step={10}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>10</span>
              <span>500</span>
            </div>
          </div>

          <div className="space-y-2 p-4 bg-primary/5 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-medium">Totalpris:</span>
              <span className="text-2xl font-bold text-primary">
                {currentPrice.toLocaleString('sv-SE')} SEK
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium flex items-center gap-1">
                <Timer className="h-4 w-4" />
                Färdig film:
              </span>
              <span className="text-lg font-semibold">
                {availableSeconds} sekunder
              </span>
            </div>
            <div className="text-sm text-muted-foreground">
              Pris per credit: {currentPricePerCredit} SEK
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};